import { LevelType } from '../types/Level';
import { DraftLevel, DraftStatus, RejectedEntry } from './types';
import { isWebAddress } from './funFact';

const STATUSES: DraftStatus[] = ['ready', 'needs-picture', 'needs-grid', 'failed'];
const LEVEL_TYPES = Object.values(LevelType) as string[];

const QUEUE_KEY = 'studio_queue_v1';
const REJECTED_KEY = 'studio_rejected_v1';

const readList = (key: string): unknown[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('Level Studio could not save to browser storage:', error);
  }
};

const isDraft = (item: unknown): item is DraftLevel => {
  if (!item || typeof item !== 'object') return false;
  const draft = item as Partial<DraftLevel>;
  return (
    typeof draft.id === 'string' &&
    LEVEL_TYPES.indexOf(draft.levelType as string) !== -1 &&
    STATUSES.indexOf(draft.status as DraftStatus) !== -1
  );
};

const stringList = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  return value.filter(item => typeof item === 'string');
};

const isGrid = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;
  const grid = value as { grid?: unknown; wordPositions?: unknown };
  const rowsOk = Array.isArray(grid.grid) && grid.grid.every(
    row => Array.isArray(row) && row.every(cell => typeof cell === 'string')
  );
  const positionsOk = Array.isArray(grid.wordPositions) && grid.wordPositions.every(position => {
    const p = position as { word?: unknown; startX?: unknown; startY?: unknown; direction?: unknown } | null;
    return !!p && typeof p.word === 'string' && typeof p.startX === 'number' &&
      typeof p.startY === 'number' && typeof p.direction === 'string';
  });
  return rowsOk && positionsOk;
};

// Stored data may be from an older version or damaged; make every field safe to render and save.
const repair = (draft: DraftLevel): DraftLevel => {
  const repaired: DraftLevel = {
    ...draft,
    flags: stringList(draft.flags) || [],
    difficulty: typeof draft.difficulty === 'number' && isFinite(draft.difficulty)
      ? Math.min(5, Math.max(1, Math.round(draft.difficulty)))
      : 1,
  };
  const words = stringList(draft.words);
  const keyLetters = stringList(draft.keyLetters);
  if (words) repaired.words = words; else delete repaired.words;
  if (keyLetters) repaired.keyLetters = keyLetters; else delete repaired.keyLetters;
  if (typeof repaired.imageCredit !== 'string' || !repaired.imageCredit) delete repaired.imageCredit;
  if (typeof repaired.funFact !== 'string' || !repaired.funFact.trim()) {
    delete repaired.funFact;
    delete repaired.funFactSource;
  }
  if (typeof repaired.funFactSource !== 'string' || !isWebAddress(repaired.funFactSource)) delete repaired.funFactSource;
  if (repaired.wordSearchGrid !== undefined && !isGrid(repaired.wordSearchGrid)) {
    delete repaired.wordSearchGrid;
    if (repaired.levelType === LevelType.WordSearch && repaired.status === 'ready') repaired.status = 'needs-grid';
  }
  return repaired;
};

const isRejected = (item: unknown): item is RejectedEntry => {
  if (!item || typeof item !== 'object') return false;
  const entry = item as Partial<RejectedEntry>;
  return typeof entry.key === 'string' && typeof entry.label === 'string';
};

export const loadQueue = (): DraftLevel[] =>
  readList(QUEUE_KEY).filter(isDraft).map(repair);

export const saveQueue = (drafts: DraftLevel[]): void => write(QUEUE_KEY, drafts);

export const removeDraft = (id: string): void => saveQueue(loadQueue().filter(draft => draft.id !== id));

export const loadRejected = (): RejectedEntry[] => readList(REJECTED_KEY).filter(isRejected);

export const addRejected = (entry: RejectedEntry): void => {
  const current = loadRejected();
  if (current.some(existing => existing.key === entry.key)) return;
  write(REJECTED_KEY, [...current, entry]);
};
